import React from 'react';
import { FaCircle, FaCrown, FaUser } from 'react-icons/fa';
import { getStatusColor } from '../../utils/statusColors';

type StatusType = 'available' | 'busy' | 'dnd';
type RoleType = 'admin' | 'member' | 'guest';
type BadgeType = 'status' | 'role';
type BadgeSize = 'sm' | 'md' | 'lg';

interface StatusBadgeProps {
  type: BadgeType;
  value: StatusType | RoleType;
  size?: BadgeSize;
  showIcon?: boolean;
  showLabel?: boolean;
  className?: string;
}

export function StatusBadge({ 
  type, 
  value, 
  size = 'md', 
  showIcon = true,
  showLabel = true,
  className = '' 
}: StatusBadgeProps) {
  
  const getStatusConfig = (status: StatusType) => {
    const colors = getStatusColor(status);
    const labels = {
      'available': 'Available',
      'busy': 'Busy', 
      'dnd': 'Do Not Disturb'
    };
    
    return {
      color: colors.text,
      bgColor: colors.bg,
      borderColor: colors.border,
      label: labels[status] || labels.available,
      icon: FaCircle
    };
  };

  const getRoleConfig = (role: RoleType) => {
    switch (role) {
      case 'admin':
        return {
          color: 'text-game-accent',
          bgColor: 'bg-gradient-to-r from-game-orange/20 to-game-red/20',
          borderColor: 'border-game-orange/30',
          label: 'Admin',
          icon: FaCrown
        };
      case 'guest':
        return {
          color: 'text-gray-300',
          bgColor: 'bg-game-medium/30',
          borderColor: 'border-game-medium/50',
          label: 'Guest',
          icon: FaUser
        };
      case 'member':
      default:
        return {
          color: 'text-gray-300',
          bgColor: 'bg-game-medium/30',
          borderColor: 'border-game-medium/50',
          label: 'Member',
          icon: FaUser
        };
    }
  };

  const getSizeClasses = (size: BadgeSize) => {
    switch (size) {
      case 'sm':
        return {
          container: 'px-1.5 py-0.5 text-xs gap-1',
          icon: 'text-xs'
        };
      case 'md':
        return {
          container: 'px-2 py-0.5 text-xs gap-1.5', 
          icon: 'text-xs'
        };
      case 'lg':
        return {
          container: 'px-3 py-1 text-sm gap-2',
          icon: 'text-sm'
        };
      default:
        return getSizeClasses('md');
    }
  };

  const config = type === 'status' ? getStatusConfig(value as StatusType) : getRoleConfig(value as RoleType);
  const sizeClasses = getSizeClasses(size);
  const IconComponent = config.icon;

  return (
    <div className={`
      inline-flex items-center
      ${sizeClasses.container}
      ${config.bgColor}
      border ${config.borderColor}
      ${config.color}
      font-semibold uppercase tracking-wider rounded-full
      ${className}
    `}>
      {showIcon && (
        <IconComponent className={`${sizeClasses.icon} ${type === 'role' ? 'text-game-golden' : ''}`} />
      )}
      {showLabel && (
        <span>{config.label}</span>
      )}
    </div>
  );
}